import React, { Component, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import Avatar, { type AppearanceType } from '@atlaskit/avatar';
import FolderIcon from '@atlaskit/icon/core/folder-closed';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import WorldIcon from '@atlaskit/icon/core/globe';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	PUBLIC_SHARE,
	PROJECT_SHARE,
	GROUP_SHARE,
	OPEN_SHARE,
	USER_SHARE,
	type SharePermission,
	PROJECT_UNKNOWN_SHARE,
} from '@atlassian/jira-shared-types/src/share-permission.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';

const assertUnreachable = (_x: never): never => {
	throw new Error("Didn't expect to get here");
};

export type Props = {
	shares: SharePermission[];
} & Intl;

// eslint-disable-next-line jira/react/no-class-components
export class Access extends Component<Props> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static defaultProps = {
		shares: [],
	};

	static renderAvatar(
		text: string,
		avatarUrl: string,
		idx: number,
		appearance: AppearanceType = 'square',
		hasMultipleProjectRoles: boolean,
	) {
		const content = (
			<>
				<AvatarContainer>
					<Avatar size="small" src={avatarUrl} appearance={appearance} />
				</AvatarContainer>
				{isVisualRefreshEnabled() ? (
					<Box
						xcss={
							hasMultipleProjectRoles
								? styles.textContainerMultipleRoles
								: styles.textContainerSingleRole
						}
					>
						{text}
					</Box>
				) : (
					<TextContainer>{text}</TextContainer>
				)}
			</>
		);

		return isVisualRefreshEnabled() ? (
			<Flex
				key={idx}
				alignItems="center"
				xcss={
					hasMultipleProjectRoles
						? styles.avatarContainerMultipleRoles
						: styles.avatarContainerSingleRole
				}
			>
				{content}
			</Flex>
		) : (
			<Container key={idx}>{content}</Container>
		);
	}

	static renderIcon(
		text: string,
		IconComponent: ComponentType<NewCoreIconProps>,
		idx = 1,
	): React.JSX.Element {
		const content = (
			<>
				<IconContainer>
					<IconComponent label="" spacing="spacious" />
				</IconContainer>
				<TextContainer>{text}</TextContainer>
			</>
		);

		return isVisualRefreshEnabled() ? (
			<Flex key={idx} alignItems="center" xcss={styles.iconContainerWrapper}>
				{content}
			</Flex>
		) : (
			<Container key={idx}>{content}</Container>
		);
	}

	render(): React.JSX.Element {
		const {
			shares,
			intl: { formatMessage },
		} = this.props;

		const unknownProjectGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
			? messages.unknownProjectGalaxia
			: messages.unknownProject;

		const content = (
			<>
				{shares.length > 0
					? shares.map((share: SharePermission, idx) => {
							switch (share.type) {
								case OPEN_SHARE:
									return Access.renderIcon(
										formatMessage(messages.myOrganizationAccess),
										OfficeBuildingIcon,
										idx,
									);
								case PUBLIC_SHARE:
									return Access.renderIcon(formatMessage(messages.publicAccess), WorldIcon, idx);
								case GROUP_SHARE:
									return Access.renderIcon(share.group.name || '', PeopleGroupIcon, idx);
								case PROJECT_SHARE: {
									const roleName = share.role
										? share.role.name
										: formatMessage(messages.allProjectRoles);
									const text = `${share.project.name}, ${roleName}`;

									return Access.renderAvatar(
										text,
										share.project.avatarUrl,
										idx,
										'square',
										shares.length > 1,
									);
								}
								case USER_SHARE: {
									return Access.renderAvatar(
										share.user.displayName,
										share.user.avatarUrl,
										idx,
										'circle',
										false,
									);
								}
								case PROJECT_UNKNOWN_SHARE:
									return Access.renderIcon(formatMessage(unknownProjectGalaxia), FolderIcon, idx);
								default:
									assertUnreachable(share);
									return null;
							}
						})
					: Access.renderIcon(formatMessage(messages.privateAccess), LockIcon)}
			</>
		);

		return isVisualRefreshEnabled() ? (
			<Flex direction="column">{content}</Flex>
		) : (
			<CellContainer>{content}</CellContainer>
		);
	}
}

export default injectIntl(Access);

const styles = cssMap({
	avatarContainerMultipleRoles: {
		paddingBottom: token('space.100'),
		height: 'auto',
	},
	avatarContainerSingleRole: {
		paddingBottom: token('space.0'),
		height: '40px',
	},
	iconContainerWrapper: {
		height: '40px',
	},
	textContainerMultipleRoles: {
		wordWrap: 'break-word',
		flexGrow: 1,
		overflow: 'hidden',
	},
	textContainerSingleRole: {
		wordWrap: 'break-word',
		flexGrow: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarContainer = styled.div({
	paddingRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div({
	paddingTop: token('space.025'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div({
	wordWrap: 'break-word',
	flexGrow: 1,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellContainer = styled.div({
	paddingTop: token('space.075'),
	display: 'flex',
	flexDirection: 'column',
});
